import { GzDialogWebComponent } from '../../../../../../ui/gzDialog';
import { GzButtonWebComponent } from '../../../../../../ui/gzButton';
import { GzDialog } from '../../../../../../ui/gzDialog/component/GzDialog';
import { ButtonColors, ButtonTypes } from '../../../../../../ui/gzButton/types';
import { GzPolicyFullPageWindowFormWebComponent } from './form';
import { OnFormValidityChange, PolicyDialog } from './form/events/events';

export interface FullPagePopupStrings {
  title: string;
  confirm: string;
  reset?: string;
  cancel: string;
  cancelConfirmation?: {
    title: string;
    body: string;
    confirm: string;
    cancel: string;
  };
}

export interface FullPagePopupConfigs {
  bodyForm: GzPolicyFullPageWindowFormWebComponent<any>;
  strings: FullPagePopupStrings;
  confirmCallbackFn: Function;
  resetCallbackFn?: (value: unknown) => void;
  shouldShowCancelConfirmation?: boolean;
  shouldShowResetButton?: boolean;
  isWindow?: boolean;
}

export class GzPolicyFullPageWindow {
  protected dialog: GzDialogWebComponent;
  private readonly strings: FullPagePopupStrings;
  protected readonly confirmCallbackFn: Function;
  private readonly resetCallbackFn: (value: unknown) => void;

  public constructor(configs: FullPagePopupConfigs) {
    this.strings = configs.strings;
    this.confirmCallbackFn = configs.confirmCallbackFn;
    this.resetCallbackFn = configs.resetCallbackFn || (() => {});

    this.buildFullPageWindow(
      configs.bodyForm,
      configs.shouldShowCancelConfirmation,
      configs.shouldShowResetButton,
      configs.isWindow
    );
  }

  public show() {
    this.dialog.open();
  }

  public buildFullPageWindow(
    bodyForm: GzPolicyFullPageWindowFormWebComponent<any>,
    shouldShowCancelConfirmation?: boolean,
    shouldShowResetButton?: boolean,
    isWindow?: boolean
  ) {
    // compute footer
    const footer = document.createElement('div');
    const footerContent = document.createElement('div');
    const addButton = new GzButtonWebComponent();
    const cancelButton = new GzButtonWebComponent();

    addButton.id = 'confirm';
    addButton.type = ButtonTypes.FULL;
    addButton.color = ButtonColors.PRIMARY;
    addButton.innerText = this.strings.confirm;
    addButton.addEventListener('click', this.confirmCallback.bind(this));
    footer.appendChild(addButton);

    cancelButton.id = 'cancel';
    cancelButton.type = ButtonTypes.OUTLINE;
    cancelButton.color = ButtonColors.SECONDARY;
    cancelButton.innerText = this.strings.cancel;
    cancelButton.addEventListener('click', this.cancelCallback.bind(this, shouldShowCancelConfirmation));

    if (shouldShowResetButton) {
      const resetButton = new GzButtonWebComponent();
      resetButton.id = 'reset';
      resetButton.type = ButtonTypes.QUIET;
      resetButton.color = ButtonColors.SECONDARY;
      resetButton.innerText = this.strings.reset || '';
      resetButton.style.marginLeft = '16px';
      resetButton.addEventListener('click', this.resetCallbackFn.bind(this));
      footerContent.appendChild(resetButton);
      footerContent.appendChild(cancelButton);
      footer.appendChild(footerContent);
    } else {
      footer.appendChild(cancelButton);
    }

    // compute body
    const windowBodyHtml = document.createElement('div');
    windowBodyHtml.style.padding =
      'calc(2 * var(--spacer)) calc(2 * var(--spacer)) calc(2 * var(--spacer)) calc(5 * var(--spacer))';
    bodyForm.id = 'body-form-component';
    bodyForm.addEventListener(
      PolicyDialog.OnFormValidityChange,
      (event: OnFormValidityChange) => (addButton.disabled = !event.detail)
    );
    windowBodyHtml.appendChild(bodyForm);

    // build dialog
    const windowConfigs: GzDialog = {
      headline: this.strings.title,
      bodyContent: windowBodyHtml,
      footerContent: footer,
      isOpen: false,
      closableOutsideBox: false,
      isWindow: isWindow ?? true, // default to true
      onCloseHandler: this.cancelCallback.bind(this, shouldShowCancelConfirmation),
    };
    this.dialog = GzDialogWebComponent.buildDialogElement(
      windowConfigs,
      document.querySelector('.mainContainer') as HTMLElement
    );
  }

  private cancelCallback(shouldShowCancelConfirmation?: boolean) {
    if (!shouldShowCancelConfirmation) {
      this.dialog.close();
      return;
    }

    const dialogBody = document.createElement('div');
    dialogBody.innerHTML = `<p>${this.strings.cancelConfirmation?.body}</p>`;

    const footer = document.createElement('div');
    footer.innerHTML = `
      <gz-button id="buttonOne" type="outline" color="negative">${this.strings.cancelConfirmation?.confirm}</gz-button>
      <gz-button id="buttonTwo" type="outline" color="secondary">${this.strings.cancelConfirmation?.cancel}</gz-button>
  `;

    const footerBtnOne = footer.querySelector('#buttonOne');
    const footerBtnTwo = footer.querySelector('#buttonTwo');

    const dialogConfigs: GzDialog = {
      headline: this.strings.cancelConfirmation?.title || '',
      bodyContent: dialogBody,
      footerContent: footer,
      isOpen: true,
    };

    const dialog = GzDialogWebComponent.buildDialogElement(
      dialogConfigs,
      document.querySelector('.mainContainer') as HTMLElement
    );

    if (footerBtnOne) {
      footerBtnOne.addEventListener('click', () => {
        dialog.dismiss();
        this.dialog.close();
      });
    }

    if (footerBtnTwo) {
      footerBtnTwo.addEventListener('click', () => {
        dialog.close();
      });
    }
  }

  protected confirmCallback() {
    // get body form
    const body = this.dialog.shadowRoot?.querySelector(
      '#body-form-component'
    ) as GzPolicyFullPageWindowFormWebComponent<any>;
    if (!body) {
      return;
    }

    const validationResponse = this.confirmCallbackFn(body.formValue);
    if (!validationResponse.valid) {
      body.setError(validationResponse.error);
      return;
    }

    this.dialog.close();
  }
}

/**
 * This class is a more complex version of the GzPolicyFullPageWindow class, because it allows for the confirmCallback to be async.
 */
export class GzPolicyFullPageWindowComplex extends GzPolicyFullPageWindow {
  protected async confirmCallback() {
    // get body form
    const body = this.dialog.shadowRoot?.querySelector(
      '#body-form-component'
    ) as GzPolicyFullPageWindowFormWebComponent<any>;
    if (!body) {
      return;
    }

    const addButton = this.dialog.shadowRoot?.querySelector('#confirm') as GzButtonWebComponent;
    addButton.setLoadingState(true);
    const validationResponse = await this.confirmCallbackFn(body.formValue);
    addButton.setLoadingState(false);

    if (!validationResponse.valid) {
      body.setError(validationResponse.error);
      return;
    }

    this.dialog.close();
  }
}
